/**
 * About page (v1.5)
 *
 * Two audiences:
 *   • Regular users  → polished, plain-language "what's new for you" notes.
 *   • Admin users    → detailed technical changelog with endpoints, root causes,
 *                       file references, and test counts.
 *
 * The release date for the CURRENT version is sourced from /api/system/version
 * (backend's DEPLOYED_AT, derived from the latest source-file mtime — so it
 * reflects the actual deploy time and persists across container restarts).
 * Older versions keep their static historical dates.
 */
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Wallet, Receipt, Upload as UploadIcon, BarChart3, LineChart, Shield,
  Settings as SettingsIcon, Globe, Repeat, Sparkles, AlertTriangle, History, ExternalLink, PlayCircle,
} from 'lucide-react';

const FALLBACK_VERSION = 'v1.7.7';
const WALKTHROUGH_URL = 'https://app.guidde.com/share/playbooks/eFSFop2spwJSPauzwuHNn3?origin=UtISP7ThlQcLHePJybYVN9Cs9Ek1&t=0&mode=videoOnly';

const FEATURES = [
  {
    icon: BarChart3,
    title: 'Dashboard & Financial Health',
    items: [
      'Monthly snapshot: income, net expenses, savings, net worth (CAD).',
      'YTD savings tile turns green when positive, red when negative.',
      'Net Worth tile shows assets in green, liabilities in red.',
      'Financial Health score with category breakdown vs benchmarks.',
      '50 / 30 / 20 budget rule visualizer (Needs / Wants / Savings).',
      'Tax-tracking mode (enable in Settings) calculates YTD tax owed on salary.',
    ],
  },
  {
    icon: Receipt,
    title: 'Transactions',
    items: [
      'Auto-categorized from PDF/CSV uploads; learns from your edits.',
      'Manual entry with optional recurring schedule (start + end date).',
      'Self-Transfer detection (UPI / IMPS / NEFT) — paired entries net to zero.',
      'Filter by source: All / Imported / Manual / Transfers.',
      'Link a transaction to an asset (SIP) or liability (repayment).',
    ],
  },
  {
    icon: UploadIcon,
    title: 'Statement Uploads',
    items: [
      'Drop PDF or CSV — AI extracts transactions, normalises merchants, dedupes by SHA-256.',
      'Auto-tagged to the correct month pair (e.g., "Apr–May 2026").',
      'Filter Upload History by the month you uploaded in.',
      'Auto-reconciles internal transfers after every upload.',
    ],
  },
  {
    icon: Wallet,
    title: 'Net Worth',
    items: [
      'Track assets and liabilities in any of 9 currencies (live FX cache).',
      'Click a card to drill into its transaction log (every SIP / repayment).',
      'Auto-rolls SIPs into asset value and repayments into liability balance.',
    ],
  },
  {
    icon: LineChart,
    title: 'Investments',
    items: [
      'YTD portfolio chart with auto-bucketed timescale (day → week → month).',
      'Smart prompts: investment-like transactions show "Add to Portfolio" or "Dismiss".',
      'Multi-currency holdings; values consolidated to your base currency.',
    ],
  },
  {
    icon: Repeat,
    title: 'Income',
    items: [
      'Recurring income forward-projects through the year automatically.',
      'Override a single month (e.g., May bonus) without breaking the recurrence.',
      'Tax tracking applies only to Salary income (per-month + YTD).',
    ],
  },
  {
    icon: Globe,
    title: 'Currency & Region',
    items: [
      'Auto-detects your country & currency on first login (silently).',
      'Settings → Region & Currency: change base currency, hit Save Profile, UI updates instantly.',
      'Supported: CAD, USD, INR, GBP, EUR, AUD, SGD, AED, JPY.',
    ],
  },
  {
    icon: SettingsIcon,
    title: 'Personalisation',
    items: [
      'Category Spending Caps with on-dashboard overage alerts.',
      'Tax tracking toggle with custom rate.',
      'Learned merchant rules (review under Settings).',
    ],
  },
  {
    icon: Shield,
    title: 'Admin',
    items: [
      'Admin users can browse any tenant\u2019s users.',
      'View a specific user\u2019s dashboard scoped to any past or future month.',
    ],
  },
];

const UNSUPPORTED = [
  'Scanned-image PDFs without an OCR layer (extraction will be empty).',
  'Statements in non-Latin scripts (Arabic, Mandarin) \u2014 transaction merchants may not normalise.',
  'Brokerage / trade-confirmation PDFs \u2014 only bank & credit-card statements are supported.',
  'Real-time market quotes for investments \u2014 values reflect what you enter / update manually.',
  'File sizes > 10 MB per upload.',
  'Encrypted / password-protected PDFs.',
];

/**
 * VERSION_NOTES — one entry per release. Two parallel lists per entry:
 *   • notes_user  : plain-language, customer-facing changes. No file paths, no
 *                   endpoints, no jargon. Each line describes a benefit the user
 *                   experiences.
 *   • notes_admin : technical changelog. Includes endpoints, root-cause analysis,
 *                   file references, test results, deploy/operate notes.
 *
 * `date` is the historical release date. The CURRENT version row replaces this
 * with the live deploy timestamp from /api/system/version.
 */
const VERSION_NOTES = [
  {
    version: 'v1.7.7',
    date: 'September 20, 2026',
    notes_user: [
      'New: Recurring repayments — schedule car payments, EMIs, rent, and subscriptions. All instances appear in your transaction list immediately; past ones update net worth right away, future ones update on their exact due date.',
      'New: Edit a recurring series end date from any month — just open any scheduled payment and change when it stops.',
      'New: Category search — type to filter the categories dropdown instead of scrolling.',
      'New: Transaction type filter — quickly show only Debits or Credits in your transaction list.',
      'New category: Alcohol added to the category list.',
      'Fix: "Alcohol" and other new categories now appear instantly after a deployment (no more empty-DB-only seeding).',
      'Performance: App uses less memory — connection pool and caches are now tightly bounded.',
    ],
    notes_admin: [
      'Feature: Recurring transactions v1 — recurring_scheduler.py daily background task; pre-generates all instances (past + future) on creation; balance_applied flag gates net worth updates by date.',
      'Feature: PUT /api/transactions/{id}/recurring-end-date — reduces or extends the series, deletes/adds instances accordingly.',
      'Feature: Category combobox — replaced Select with Popover+Command (Shadcn) for searchable filtering.',
      'Feature: Type filter pills (All Types / Debits / Credits) in TransactionsPage toolbar.',
      'Fix: Category seeding changed from if-empty to idempotent bulk_write $setOnInsert — new categories always added on startup.',
      'Fix: /api/categories now returns in CATEGORIES list order (not MongoDB insertion order).',
      'Fix: @require_pro decorator rewrote wrapper to **kwargs — was crashing POST endpoints where request was not first param.',
      'Perf: MongoDB maxPoolSize 30→10, minPoolSize 5→2, maxIdleTimeMS 30s→15s.',
      'Perf: cpu_cache.py hard-capped at MAX_CACHE_ENTRIES=200 with LRU eviction + proactive TTL sweep on every write.',
      'Perf: memory_monitor.py warning threshold 85%→75%; GC + cache clear at warning; OS page cache drop at critical; interval 10 min→5 min.',
      'Perf: Disk cleanup changed from weekly (90-day retention) to daily orphan check + weekly 30-day old-file purge.',
      'DB index: [auto_generated, balance_applied, date] added for recurring balance scheduler query.',
      'Files: recurring_scheduler.py (new), transactions.py (+_pre_generate_instances, +update_recurring_end_date), models.py (+recurring_frequency), server.py (v1.7.7, idempotent seeding), cpu_cache.py (rewrite), memory_monitor.py (rewrite), database.py (pool reduction), AboutPage.js (this entry).',
      'Testing: iteration_60, 61, 62 — all passed.',
      'Backend: Version bumped to v1.7.7.',
    ],
  },
  {
    version: 'v1.7.0',
    date: 'December 9, 2026',
    notes_user: [
      'Premium features (Net Worth, Investments, Income) now require active subscription after 90-day trial ends.',
      'File uploads migrated to secure cloud storage for better reliability and performance.',
      'Improved security with backend validation of subscription status.',
      'Trial countdown and upgrade prompts display accurate remaining days.',
    ],
    notes_admin: [
      'CRITICAL: Subscription enforcement implemented - @require_pro decorator added to all premium endpoints.',
      'Backend: Added @require_pro to 17 premium endpoints across networth.py (12 endpoints) and income.py (5 endpoints).',
      'Backend: Users with expired trials (> 90 days from created_at) receive 403 Forbidden on premium API calls.',
      'Backend: Trial logic validates: admin bypass, subscription_waived flag, trial_active check, subscription_status check.',
      'Backend: Error response includes trial_end_date, days_left, and upgrade_url for client-side handling.',
      'Feature: Object Storage migration - statement uploads now use Emergent Object Storage instead of local disk.',
      'Backend: New storage.py module with put_object(), get_object(), init_storage() functions.',
      'Backend: statements.py refactored - uploads save to cloud path themoneymap/uploads/{user_id}/{uuid}.{ext}.',
      'Backend: Soft-delete pattern implemented - statements marked is_deleted: True (no physical deletion).',
      'Backend: Download endpoint (/api/statements/{id}/download) retrieves files from object storage.',
      'Backend: List/Get endpoints filter out soft-deleted statements (is_deleted != True).',
      'Backend: Storage initialized on startup - logs "✅ Storage initialized successfully".',
      'Frontend: Net Worth graph hidden from regular users, admin-only "Coming Soon" placeholder with badge.',
      'Frontend: ProFeatureGate checks user.has_pro_access from /api/auth/me response.',
      'Frontend: TrialBanner shows "trial ended" for expired trials, "X days left" for active trials < 7 days.',
      'Security: Prevents trial bypass via direct API calls - both frontend and backend now enforce paywall.',
      'Files modified: subscription_check.py (has_pro_access logic), networth.py (+12 @require_pro), income.py (+5 @require_pro), storage.py (new), statements.py (object storage), server.py (storage init), NetWorthPage.js (admin-only graph).',
      'Testing: Backend restarted successfully, all services running, no lint errors.',
      'Deployment: Ready for immediate production deployment with strict enforcement.',
      'Backend: Version bumped to v1.7.0.',
    ],
  },
  {
    version: 'v1.5.1',
    date: 'August 11, 2026',
    notes_user: [
      'New Feedback page - easily report bugs or request features directly from the sidebar.',
      'Custom date range filtering on Transactions page - view any date range you need.',
      'Security badges showing Google Cloud protection on login page and footer.',
      'Upload notifications now show transaction count automatically without page refresh.',
      'Improved currency detection for Canadian credit unions and multi-currency statements.',
      'Better handling of airline tickets and multi-line transactions in statement parsing.',
    ],
    notes_admin: [
      'Feature: New Feedback page (/feedback) with bug report and feature request forms.',
      'Backend: POST /api/support/bug-report and /api/support/feature-request endpoints with email delivery to info@themoneymap.online.',
      'Backend: Unique ticket IDs (B001, B002 for bugs; F001, F002 for features) stored in support_tickets collection.',
      'Backend: Rate limiting - max 3 bug reports + 3 feature requests per user per day (HTTP 429).',
      'Feature: Custom date range filtering added to transactions - date_from and date_to query parameters on GET /api/transactions.',
      'Feature: Google Cloud security badges on LoginPage.js (bottom badge) and Layout.js footer (lock icon + Google logo).',
      'Bug fix: Upload status polling - fixed field name mismatch (s.id vs s._id) that prevented transaction count from updating.',
      'Bug fix: Currency detection - reordered priority to check Canadian banks (CREDIT UNION, TD, RBC) before Indian banks to prevent false INR detection.',
      'Bug fix: Currency detection - added user base_currency as fallback when no explicit indicators found (respects INR/USD/CAD user preference).',
      'Bug fix: Statement parsing - negative amounts on credit card statements now correctly classified as returns/refunds (not purchases).',
      'Bug fix: Multi-line transactions - increased OVERLAP_LINES from 3 to 20 and added is_continuation_line() to prevent splitting airline tickets across chunks.',
      'Enhancement: detect_currency_from_text() now async and uses header-only checking (first 2000 chars) to avoid false positives from transaction merchant names.',
      'UI: Feedback navigation item added to sidebar with MessageSquare icon.',
      'UI: Support page cleaned up - removed duplicate feedback section, now focuses only on donations/subscriptions.',
      'Files modified: support.py (new), FeedbackPage.js (new), transactions.py (date params), ai_service.py (currency + chunking), TransactionsPage.js (date range UI), UploadPage.js (polling fix), LoginPage.js + Layout.js (security badges).',
      'Testing: 100% pass rate - 41/41 frontend tests, 13/13 backend tests across all features.',
      'Backend: Version bumped to v1.5.1.',
    ],
  },
  {
    version: 'v1.5',
    date: 'July 30, 2026',
    notes_user: [
      'Biweekly income frequency option for twice-monthly paychecks.',
      'Dining Out category groups Coffee, Restaurants, and Takeout automatically.',
      'Minor bug fixes and performance improvements.',
    ],
    notes_admin: [
      'Feature: Email verification now required for new signups (blocks login until verified).',
      'Feature: Biweekly income frequency added - creates 2 entries per month (1st and 15th).',
      'Feature: Dining Out category grouping - combines Coffee, Restaurants, Takeout in single view.',
      'Feature: Admin Grant/Revoke Pro access - manually give free Pro to individual users.',
      'Enhancement: Subscribe button text changed from "Start Free Trial" to "Subscribe" for clarity.',
      'Enhancement: Login response now includes subscription data (has_pro_access, trial_days_left) to reduce API calls.',
      'Enhancement: Admin credentials simplified to username "admin" / password "<REDACTED>".',
      'Bug fix: Trial status banner race condition - was showing "expired" message briefly during load, now waits for data.',
      'Bug fix: Trial banner showing repeatedly - now shows once per login session (sessionStorage flag).',
      'Bug fix: 3rd login welcome popup showing on every dashboard visit - now shows once per session.',
      'Bug fix: Email verification was not enforced on login - unverified users could access app.',
      'Security: Unverified email users now blocked from login with clear error message.',
      'Performance: Dashboard page loads optimized with caching and batching.',
      'Performance: Supervisor log rotation enabled to prevent disk overflow.',
      'Backend: Version bumped to v1.5.0.',
    ],
  },
  {
    version: 'v1.4.5',
    date: 'Feb 12, 2026',
    notes_user: [
      'The Release date on this page now matches the day a new version actually goes live for you.',
      'Polished release notes: cleaner, plainer language about what is new for you in each update.',
      'Admins see a more detailed technical view of the same release; everyday users see this friendlier summary.',
      'Server-health dashboard cards now clearly distinguish disk used vs free, and show the database footprint as a percentage.',
    ],
    notes_admin: [
      'Backend DEPLOYED_AT now derives from max mtime of /app/backend/*.py (persists across pod restarts; updates only when code ships). Returned by GET /api/system/version.',
      'AboutPage split into role-aware rendering: useAuth().user.role gates notes_admin vs notes_user; admins also see endpoint/file references.',
      'AdminHealthPage tiles relabeled "Disk used (/app)" + sub-line shows `X.XX GB used · Y.YY GB free · Z.ZZ GB total`. DB tile now headlines `pct_of_disk` (db.totalSize / disk_app.total).',
      'admin_health.py: new field `database.pct_of_disk` (4-decimal precision).',
      'No new tests this iteration — all 25 prior pytests (test_p8 / p9 / p11 / p12) still pass.',
    ],
  },
  {
    version: 'v1.4.4',
    date: 'Feb 12, 2026',
    notes_user: [
      'Resolved an issue where some accounts stayed locked after an admin reset their password — sign-in works first try now.',
      'New Admin "Health" area shows live server, database and request-latency metrics.',
      'Statement uploads now clearly indicate that only credit-card statements are supported in this release; debit / savings files are politely rejected instead of producing wrong data.',
      'Release-note dates are anchored to actual release dates — no more day-to-day drift.',
    ],
    notes_admin: [
      'gkoramutla root cause: login_attempts records (identifier = `<ip>:<username>`) were never cleared on admin password-reset or user-delete, so the 5+ failed-attempt 429 lockout persisted across reset and across delete-then-recreate with the same username.',
      'Fix: admin.reset_password and admin.delete_user now `login_attempts.delete_many({"identifier": /:<username>$/})`. Reset response includes `lockouts_cleared`.',
      'New endpoint: POST /api/admin/users/{id}/unlock + an Unlock button on every Admin user row.',
      'Admin Health: GET /api/admin/health/summary (app/system/database/collections/app_counts/recent_login_records); GET /api/admin/health/latency (mongo.ping + count probes). Admin-only via require_admin.',
      'Statement gate: new classify_statement_type(text) heuristic; PDF and CSV processors reject "debit_or_savings" with HTTP 400. Outer except re-raises HTTPException so the status code reaches the client.',
      'Tests: tests/test_p12_v144_changes.py — 8 new (3 lockout, 3 health, 2 CC-only). 25/25 backend + 13/13 frontend in iteration_20.',
    ],
  },
  {
    version: 'v1.4.3',
    date: 'Feb 11, 2026',
    notes_user: [
      'Edit a single month of a recurring expense (e.g., this May’s rent) without disturbing the rest of the schedule.',
      'Contributions in one currency to an account in another now convert correctly — for example, a CAD top-up to an INR investment account adds the right rupee amount, not the raw CAD number.',
      'Transaction logs inside Net Worth now show each amount in the destination account’s currency.',
    ],
    notes_admin: [
      'Per-month override: TransactionCreate.recurring_source_id stores the override; list endpoint skips virtual projection for any month with an existing override.',
      'ledger.apply_balance_delta now FX-converts in all 4 paths (asset/liability × repayment/non-repayment × investment/non-investment) via fx_service.convert.',
      'networth.get_asset|liability_transactions enrich each txn with converted_amount + converted_currency; totals use the converted figures.',
      'Tests: tests/test_p11_v143_changes.py — 4 new (override projection, CAD→INR asset, CAD→INR liability, /system/version shape).',
    ],
  },
  {
    version: 'v1.4.2',
    date: 'Feb 11, 2026',
    notes_user: [
      'Schedule recurring manual entries (rent, EMIs, subscriptions) with a start and optional end date.',
      'New About section with the feature list, supported and unsupported scenarios, and release notes.',
      'Filter your upload history by the month you uploaded in.',
      'Dashboard tiles now use colour: green when savings are positive, red when negative; assets in green, liabilities in red.',
      'Admins can browse any user’s dashboard month-by-month.',
      'Investments can now be dismissed when a suggested entry isn’t a real holding.',
      'Settings: changing your default currency immediately re-formats every amount across the app.',
    ],
    notes_admin: [
      '11 user-feedback items shipped: recurring transactions + virtual projection in list endpoint, new /about route, upload-month filter, dashboard tile colour logic, admin user-month picker (/api/admin/users/{id}/available-months), investment-prompt dismissal endpoint, NetWorth Dialogs moved outside Tabs (Item §8 fix), Credit Card Debt card removed from analytics, subscribeDisplayCurrency / useDisplayCurrency hook for live re-format.',
      'iteration_17 — 100% backend + 100% frontend.',
    ],
  },
  {
    version: 'v1.4.1',
    date: 'Feb 11, 2026',
    notes_user: [
      'Internal improvements that make the dashboard a little snappier and more reliable.',
    ],
    notes_admin: [
      'Extracted dashboard helpers to /app/backend/services/analytics_service.py (merge_food_categories, get_income_for_month, CC_LIABILITY_TYPES, is_credit_card_liability).',
      'Replaced silent catch {} blocks (AuthContext detect-location, logout, UploadPage list-statements) with console.warn + error context.',
    ],
  },
  {
    version: 'v1.4',
    date: 'Feb 11, 2026',
    notes_user: [
      'Your country and preferred currency are auto-detected on first login.',
      'For India, the app now detects UPI / IMPS / NEFT self-transfers and avoids double-counting them in spending.',
      'A new "Detect now" button in Settings re-checks your region and currency.',
    ],
    notes_admin: [
      'GET /api/auth/detect-location uses free ip-api.com (no key) to suggest {country_code, country, currency}.',
      'reconcile.py — name-fragment + amount + ±2 day matching for internal transfers. Auto-runs after statement upload + manual button on Transactions page. POST /api/transactions/{id}/unmark-transfer reverses a pairing.',
    ],
  },
  {
    version: 'v1.3.1',
    date: 'Feb 11, 2026',
    notes_user: [
      'Click any asset or liability in Net Worth to see every contribution and repayment that affected it.',
      'Tax is now calculated only on salary income, by month and year-to-date.',
      'Change Password moved to the top-right user menu, where you’d expect it.',
    ],
    notes_admin: [
      'New endpoints: GET /api/networth/assets|liabilities/{id}/transactions (drill-down + totals).',
      'Credit Card debt metric now strictly filters by liability_type ∈ {credit card, credit_card, cc}.',
      'Per-month tax breakdown limited to source == "Salary".',
    ],
  },
];

export default function AboutPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [versionInfo, setVersionInfo] = useState({ version: FALLBACK_VERSION, deployed_at: null });
  useEffect(() => {
    api.get('/api/system/version')
      .then(({ data }) => setVersionInfo(data))
      .catch((err) => console.warn('[about] version fetch failed:', err?.message || err));
  }, []);

  const currentVersion = versionInfo.version;
  // Current row: prefer the live deploy timestamp (always reflects the latest deploy);
  // fall back to the static date in VERSION_NOTES if the API didn't respond.
  const currentNote = VERSION_NOTES.find((v) => v.version === currentVersion);
  const liveDeployDate = versionInfo.deployed_at
    ? new Date(versionInfo.deployed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;
  const currentDate = liveDeployDate || currentNote?.date || '—';

  return (
    <div className="space-y-8" data-testid="about-page">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight font-['Outfit']">About TheMoneyMap</h1>
          <p className="text-sm text-muted-foreground mt-1">{isAdmin ? 'Feature handbook and detailed release notes.' : 'What TheMoneyMap can do for you, and what is coming next.'}</p>
        </div>
        <Badge variant="outline" className="text-xs" data-testid="about-version-badge">{currentVersion} · {liveDeployDate ? 'Deployed' : 'Released'} {currentDate}</Badge>
      </div>

      {/* Walkthrough section */}
      <Card className="border border-primary/20 bg-primary/5 shadow-none" data-testid="about-walkthrough">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
              <PlayCircle className="h-6 w-6 text-primary" />
            </div>
            <div className="flex-1 space-y-3">
              <div>
                <h3 className="text-base font-semibold">Learn More</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  New to TheMoneyMap? Watch our comprehensive video walkthrough to get started quickly.
                </p>
              </div>
              <Button 
                size="sm" 
                onClick={() => window.open(WALKTHROUGH_URL, '_blank', 'noopener,noreferrer')}
                data-testid="about-walkthrough-btn"
              >
                Application Walkthrough
                <ExternalLink className="h-3.5 w-3.5 ml-1" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Feature catalog */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="about-features">
        {FEATURES.map((f) => {
          const Icon = f.icon;
          return (
            <Card key={f.title} className="border border-border shadow-none">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Icon className="h-4 w-4 text-primary" />
                  {f.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-1.5">
                  {f.items.map((it) => (
                    <li key={it} className="text-xs text-muted-foreground leading-relaxed flex gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Unsupported scenarios */}
      <Card className="border border-amber-200 dark:border-amber-900/40 bg-amber-50/40 dark:bg-amber-900/10 shadow-none" data-testid="about-unsupported">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2 text-amber-900 dark:text-amber-300">
            <AlertTriangle className="h-4 w-4" />
            Currently unsupported
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-1.5">
            {UNSUPPORTED.map((u) => (
              <li key={u} className="text-xs text-amber-800/90 dark:text-amber-200/80 leading-relaxed flex gap-2">
                <span className="mt-0.5">•</span>
                <span>{u}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {/* Version notes — role-aware (admin sees technical, user sees plain language) */}
      <Card className="border border-border shadow-none" data-testid="about-version-notes">
        <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <History className="h-4 w-4 text-primary" />
            {isAdmin ? 'Release Notes — Technical' : 'What’s New'}
          </CardTitle>
          {isAdmin && <Badge variant="outline" className="text-[10px]" data-testid="about-admin-view">Admin view</Badge>}
        </CardHeader>
        <CardContent>
          <div className="space-y-5">
            {VERSION_NOTES.map((v, idx) => {
              const isCurrent = v.version === currentVersion || (idx === 0 && !VERSION_NOTES.some((x) => x.version === currentVersion));
              const rowDate = isCurrent ? currentDate : v.date;
              const items = isAdmin ? (v.notes_admin || []) : (v.notes_user || []);
              if (items.length === 0) return null;
              return (
                <div key={v.version} className={`pl-3 ${isCurrent ? 'border-l-2 border-primary' : 'border-l border-border'}`} data-testid={`version-row-${v.version}`}>
                  <div className="flex items-baseline gap-2 flex-wrap mb-1.5">
                    <Badge variant={isCurrent ? 'default' : 'outline'} className="text-[10px]" data-testid={`version-badge-${v.version}`}>{v.version}</Badge>
                    {rowDate && <span className="text-[11px] text-muted-foreground">{rowDate}</span>}
                    {isCurrent && <span className="text-[10px] text-primary font-medium flex items-center gap-1"><Sparkles className="h-3 w-3" />Current</span>}
                  </div>
                  <ul className="space-y-1">
                    {items.map((h) => (
                      <li key={h} className="text-xs text-muted-foreground leading-relaxed flex gap-2">
                        <span className="text-primary/60 mt-0.5">•</span>
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <p className="text-[11px] text-muted-foreground text-center">
        Questions? Email{' '}
        <a href="mailto:info@themoneymap.online" className="text-primary hover:underline">info@themoneymap.online</a>
      </p>
    </div>
  );
}
